import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Silk from './Silk';
import LightRaysAdvanced from './LightRaysAdvanced';
import androidQR from '../photo/android.png';
import appleQR from '../photo/apple.png';

const DownloadSection = styled.section`
  min-height: ${props => (props.$embedded ? 'auto' : '100vh')};
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${props => (props.$embedded ? '0' : '0 20px')};
  position: relative;
  overflow: hidden;
`;

const DownloadContent = styled(motion.div)`
  max-width: 800px;
  z-index: 2;
`;

const BackButton = styled(motion.button)`
  position: fixed;
  top: 30px;
  left: 30px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 9999;
  
  @media (max-width: 768px) {
    top: 15px;
    left: 15px;
    padding: 8px 16px;
    font-size: 14px;
    z-index: 9999;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const Title = styled(motion.h1)`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  margin-bottom: 16px;
  color: ${props => (props.$embedded ? '#1a1a1e' : '#ffffff')};
  text-shadow: ${props => (props.$embedded ? 'none' : '0 0 30px rgba(255, 255, 255, 0.5)')};
  
  @media (max-width: 768px) {
    margin-top: ${props => (props.$embedded ? '0' : '60px')};
  }
`;

const Subtitle = styled(motion.p)`
  font-size: clamp(1rem, 2vw, 1.2rem);
  margin-bottom: ${props => (props.$embedded ? '40px' : '60px')};
  color: ${props => (props.$embedded ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.9)')};
  font-weight: 400;
  line-height: 1.6;
`;

const QRContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  gap: 60px;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 40px;
  }
`;

const QRCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
  }
`;

const QRImage = styled.img`
  width: 200px;
  height: 200px;
  border-radius: 10px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    width: 180px;
    height: 180px;
  }
`;

const QRTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 10px;
`;

const QRDescription = styled.p`
  color: #666;
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 20px;
`;

const StoreButton = styled.a`
  display: inline-block;
  background: linear-gradient(135deg, #007AFF, #0051D5);
  color: white;
  text-decoration: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 122, 255, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 122, 255, 0.4);
    text-decoration: none;
    color: white;
  }
  
  &.android {
    background: linear-gradient(135deg, #34A853, #0F9D58);
    box-shadow: 0 4px 15px rgba(52, 168, 83, 0.3);
    
    &:hover {
      box-shadow: 0 6px 20px rgba(52, 168, 83, 0.4);
    }
  }
`;

const MobileMessage = styled.div`
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 12px;
  padding: 20px;
  margin-top: 40px;
  color: #856404;
  font-size: 1rem;
  text-align: center;
  
  @media (min-width: 769px) {
    display: none;
  }
  
  h4 {
    color: #856404;
    margin-bottom: 10px;
    font-size: 1.1rem;
  }
  
  p {
    margin-bottom: 15px;
    line-height: 1.5;
  }
`;

const MobileButtonContainer = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
`;

export const Download = ({ onBack, embedded = false }) => {
  const content = (
    <DownloadContent>
      <Title
        $embedded={embedded}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        Uygulamayı İndir
      </Title>

      <Subtitle
        $embedded={embedded}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        QR kodları okutarak Altın Mobile uygulamasını cihazınıza indirin ve demo sürümünü deneyin
      </Subtitle>

      <QRContainer
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
          <QRCard
            whileHover={{ scale: 1.05 }}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <QRImage src={androidQR} alt="Android QR Code" />
            <QRTitle>Android</QRTitle>
            <QRDescription>
              Google Play Store'dan<br />
              Altın Mobile'ı indirin
            </QRDescription>
            <StoreButton 
              href="https://play.google.com/store/apps/details?id=com.altinygt.altinapp&hl=tr"
              target="_blank"
              rel="noopener noreferrer"
              className="android"
            >
              📱 Google Play'de Aç
            </StoreButton>
          </QRCard>

          <QRCard
            whileHover={{ scale: 1.05 }}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
          >
            <QRImage src={appleQR} alt="Apple QR Code" />
            <QRTitle>iOS</QRTitle>
            <QRDescription>
              App Store'dan<br />
              Altın Mobile'ı indirin
            </QRDescription>
            <StoreButton 
              href="https://apps.apple.com/tr/app/alt%C4%B1n-mobile/id6748850778?l=tr"
              target="_blank"
              rel="noopener noreferrer"
            >
              🍎 App Store'da Aç
            </StoreButton>
          </QRCard>
        </QRContainer>

        <MobileMessage>
          <h4>📱 Mobil Cihazdan mı Erişiyorsunuz?</h4>
          <p>QR kod okutmaya gerek yok! Aşağıdaki butonlarla direkt uygulama mağazalarına gidin:</p>
          <MobileButtonContainer>
            <StoreButton 
              href="https://play.google.com/store/apps/details?id=com.altinygt.altinapp&hl=tr"
              target="_blank"
              rel="noopener noreferrer"
              className="android"
            >
              📱 Google Play'de Aç
            </StoreButton>
            <StoreButton 
              href="https://apps.apple.com/tr/app/alt%C4%B1n-mobile/id6748850778?l=tr"
              target="_blank"
              rel="noopener noreferrer"
            >
              🍎 App Store'da Aç
            </StoreButton>
          </MobileButtonContainer>
        </MobileMessage>
    </DownloadContent>
  );

  if (embedded) {
    return <DownloadSection $embedded>{content}</DownloadSection>;
  }

  return (
    <DownloadSection>
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
        <Silk
          speed={7.3}
          scale={1.7}
          color="#103cb9"
          noiseIntensity={0}
          rotation={0}
        />
      </div>

      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1, opacity: 0.6 }}>
        <LightRaysAdvanced
          raysOrigin="top-center"
          raysColor="#ffffff"
          raysSpeed={1.5}
          lightSpread={0.8}
          rayLength={1.2}
          followMouse={true}
          mouseInfluence={0.1}
          noiseAmount={0.1}
          distortion={0.05}
          className="hero-rays"
        />
      </div>

      {onBack && (
        <BackButton
          onClick={onBack}
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          ← Ana Sayfa
        </BackButton>
      )}

      {content}
    </DownloadSection>
  );
};
